/* eslint-disable react-hooks-extra/no-direct-set-state-in-use-effect */
import { createFileRoute, Link, useSearch } from '@tanstack/react-router'
import { ChevronRight, Loader2 } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import Footer from '@/components/footer'
import Header from '@/components/Header'
import BookingForm from '@/components/rooms/booking-form'
import RoomDetailsTabs from '@/components/rooms/room-details-tabs'
import RoomGallery from '@/components/rooms/room-gallery'
import { Button } from '@/components/ui/button'
import { useGetGuestHouseImages, useGetRoomById } from '@/utils/api'

// Component to handle image fetching for the guest house
function GuestHouseImageFetcher({
  guestHouseId,
  onImagesLoaded,
}: {
  guestHouseId: number
  onImagesLoaded: (id: number, images: string[]) => void
}) {
  const { data: imagesResponse } = useGetGuestHouseImages(guestHouseId, {
    page: 0,
    size: 10,
  })

  useEffect(() => {
    if (imagesResponse?.data?.content) {
      const imageUrls = imagesResponse.data.content
        .map(img => img.imageUrl)
        .filter((url): url is string => typeof url === 'string')
      onImagesLoaded(guestHouseId, imageUrls)
    }
  }, [imagesResponse, guestHouseId, onImagesLoaded])

  return null
}

export const Route = createFileRoute('/_landing/rooms/$roomId')({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      checkIn: (search.checkIn as string) || '',
      checkOut: (search.checkOut as string) || '',
      adults: Number(search.adults) || 1,
      children: Number(search.children) || 0,
    }
  },
})

function RouteComponent() {
  const { roomId } = Route.useParams()
  const searchParams = useSearch({ from: '/_landing/rooms/$roomId' })
  const [guestHouseImages, setGuestHouseImages] = useState<Record<number, string[]>>({})
  const [roomWithImages, setRoomWithImages] = useState<any>(null)

  // Check if we have date filters

  // Fetch room by ID - this is the primary method
  const {
    data: roomResponse,
    isLoading,
    error,
  } = useGetRoomById(Number(roomId), {
    query: {
      enabled: !!(roomId && !Number.isNaN(Number(roomId))),
    },
  })

  // Extract room from response
  const room = roomResponse?.data

  const actualIsLoading = isLoading
  const actualError = error

  const handleImagesLoaded = useCallback((id: number, images: string[]) => {
    setGuestHouseImages(prev => ({
      ...prev,
      [id]: images,
    }))
  }, [])

  // Update room with images when images are loaded
  useEffect(() => {
    if (room && room.guestHouseId !== undefined && guestHouseImages[room.guestHouseId]) {
      const images = guestHouseImages[room.guestHouseId]
      setRoomWithImages({
        ...room,
        images,
        primaryImage:
          images[0] || `/placeholder.svg?height=400&width=600&text=${encodeURIComponent(room.roomNumber ?? '')}`,
      })
    }
    else if (room) {
      setRoomWithImages({
        ...room,
        images: [],
        primaryImage: `/placeholder.svg?height=400&width=600&text=${encodeURIComponent(room.roomNumber ?? '')}`,
      })
    }
  }, [room, guestHouseImages])

  // Format currency
  const formatCurrency = (amount: number, currencyCode = 'UGX') => {
    if (currencyCode === 'UGX') {
      return new Intl.NumberFormat('en-UG', {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount)
    }

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
    }).format(amount)
  }

  // Loading state
  if (actualIsLoading) {
    return (
      <>
        <Header />
        <div className="flex justify-center items-center min-h-screen">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
            <p className="text-gray-600">Loading room details...</p>
          </div>
        </div>
        <Footer />
      </>
    )
  }

  // Error state
  if (actualError) {
    return (
      <>
        <Header />
        <div className="container mx-auto px-4 py-12 text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error loading room details</h2>
          <p className="text-gray-600 mb-8">Please try again later or go back to room search.</p>
          <Link to="/rooms" search={{ ...searchParams, page: 0 }}>
            <Button className="bg-primary hover:bg-pink-600 text-white">Back to Rooms</Button>
          </Link>
        </div>
        <Footer />
      </>
    )
  }

  // Room not found
  if (!room) {
    return (
      <>
        <Header />
        <div className="container mx-auto px-4 py-12 text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Room not found</h2>
          <p className="text-gray-600 mb-8">The room you're looking for doesn't exist or is no longer available.</p>
          <Link to="/rooms" search={{ ...searchParams, page: 0 }}>
            <Button className="bg-primary hover:bg-pink-600 text-white">Back to Rooms</Button>
          </Link>
        </div>
        <Footer />
      </>
    )
  }

  // Find the active rate, fallback to first rate
  const activeRate = room.rates?.find(rate => rate.active === true) || room.rates?.[0]

  const price = activeRate?.amount || 0
  const currency = activeRate?.currency || 'UGX'

  return (
    <>
      <Header />

      {/* Render image fetcher for this room's guest house */}
      {room.guestHouseId && (
        <GuestHouseImageFetcher guestHouseId={room.guestHouseId} onImagesLoaded={handleImagesLoaded} />
      )}

      {/* Page Banner */}
      <section className="relative h-[300px] ">
        <img
          style={{ height: 300, width: 2000 }}
          src={roomWithImages?.primaryImage || '/landscape.webp'}
          alt={`${room.guestHouse} - ${room.roomNumber}`}
          className="object-cover blur-sm scale-100"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-black/30 flex flex-col items-center justify-center text-white">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-4xl font-bold mb-4 text-primary">
              {room.guestHouse}
              {' '}
              -
              {room.roomNumber}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Link href="/" className="hover:text-orange-500 transition-colors" to="/">
              Home
            </Link>
            <ChevronRight className="h-4 w-4" />
            <Link
              href="/rooms"
              className="hover:text-orange-500 transition-colors"
              to="/rooms"
              search={{ ...searchParams, page: 0 }}
            >
              Rooms & Suites
            </Link>
            <ChevronRight className="h-4 w-4" />
            <span>{room.roomNumber}</span>
          </div>
        </div>
      </section>

      {/* Room Details */}
      <section className="container mx-auto px-4 py-12">
        <div className="flex flex-col lg:flex-row justify-between items-start mb-8">
          <div>
            <h2 className="text-3xl font-bold text-blue-700 mb-2">
              {room.guestHouse}
              {' '}
              -
              {room.roomNumber}
            </h2>
            <div className="flex items-center gap-4 text-gray-600"></div>
          </div>
          <div className="text-right mt-4 lg:mt-0">
            <p className="text-2xl font-bold text-blue-700">
              <span className="text-primary">{formatCurrency(price, currency)}</span>
              <span className="text-sm font-normal text-gray-500 block">per night</span>
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="grid grid-cols-1 md:grid-cols-5">
              <div className="md:col-span-4">
                <RoomGallery
                  images={roomWithImages?.images || []}
                  roomName={`${room.guestHouse} - ${room.roomNumber}`}
                />
              </div>
              <div className="hidden md:block">
                <div className="relative h-full w-full rounded-lg overflow-hidden">
                  {/* Optionally add additional image or details here */}
                </div>
              </div>
            </div>

            <div className="mt-8">
              <RoomDetailsTabs
                room={{
                  id: room.id ?? 0,
                  roomNumber: room.roomNumber ?? '',
                  maxOccupancy: room.maxOccupancy ?? 1,
                  bedType: typeof room.bedType === 'string' ? room.bedType : (room.bedType ?? '').toString(),
                  status: typeof room.status === 'string' ? room.status : (room.status ?? '').toString(),
                  guestHouse: room.guestHouse ?? '',
                  guestHouseId: room.guestHouseId ?? 0,
                  rates: room.rates?.map(rate => ({
                    amount: rate.amount ?? 0,
                    currency: rate.currency ?? 'UGX',
                    occupancyType: rate.occupancyType ?? '',
                  })),
                }}
              />
            </div>
          </div>

          <div>
            <div className="sticky top-4">
              <BookingForm
                room={{
                  id: room.id ?? 0,
                  roomNumber: room.roomNumber ?? '',
                  maxOccupancy: room.maxOccupancy ?? 1,
                  guestHouse: room.guestHouse ?? '',
                  bookingDetails: (room.bookingDetails || []).map((detail: any) => ({
                    ...detail,
                    id: detail.id ?? 0,
                  })),
                  rates: room.rates?.map(rate => ({
                    id: rate.id ?? 0,
                    amount: rate.amount ?? 0,
                    currency: rate.currency ?? 'UGX',
                  })),
                }}
                searchParams={searchParams}
                price={price}
                currency={currency}
              />
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </>
  )
}
